/**
 * Persona classification — frontend mirror of src/worker/lib/personas.ts
 * Research-backed from DCVFD FORGE analysis — see docs/kb/personas.md
 */
export type Persona = "zoe" | "michael" | "xavier" | "barbara" | "unknown";

export interface PersonaProfile {
  id: Persona;
  label: string;
  ageMin: number;
  ageMax: number;
  decisionTimelineWeeks: { min: number; max: number };
  primaryChannels: string[];
  color: string;
  imageUrl: string;
}

export const PERSONAS: Record<Persona, PersonaProfile> = {
  zoe: {
    id: "zoe",
    label: "Zoe (Gen Z)",
    ageMin: 16,
    ageMax: 27,
    decisionTimelineWeeks: { min: 2, max: 4 },
    primaryChannels: ["TikTok", "Instagram", "YouTube"],
    color: "#8B5CF6", // purple
    imageUrl: "/images/personas/zoe.jpg",
  },
  michael: {
    id: "michael",
    label: "Michael (Millennial)",
    ageMin: 28,
    ageMax: 43,
    decisionTimelineWeeks: { min: 4, max: 8 },
    primaryChannels: ["Facebook", "Instagram", "LinkedIn", "Email"],
    color: "#2563EB", // blue
    imageUrl: "/images/personas/michael.jpg",
  },
  xavier: {
    id: "xavier",
    label: "Xavier (Gen X)",
    ageMin: 44,
    ageMax: 59,
    decisionTimelineWeeks: { min: 6, max: 12 },
    primaryChannels: ["Facebook", "Email", "Direct"],
    color: "#0E403A", // brand green
    imageUrl: "/images/personas/xavier.jpg",
  },
  barbara: {
    id: "barbara",
    label: "Barbara (Boomer)",
    ageMin: 60,
    ageMax: 75,
    decisionTimelineWeeks: { min: 8, max: 16 },
    primaryChannels: ["Facebook", "Email", "Direct Mail", "Phone"],
    color: "#D97706", // amber
    imageUrl: "/images/personas/barbara.jpg",
  },
  unknown: {
    id: "unknown",
    label: "Unknown",
    ageMin: 0,
    ageMax: 0,
    decisionTimelineWeeks: { min: 0, max: 0 },
    primaryChannels: [],
    color: "#6B7280",
    imageUrl: "",
  },
};

export function classifyByAge(age: number | null | undefined): Persona {
  if (age === null || age === undefined || !Number.isFinite(age))
    return "unknown";
  if (age >= 16 && age <= 27) return "zoe";
  if (age >= 28 && age <= 43) return "michael";
  if (age >= 44 && age <= 59) return "xavier";
  if (age >= 60 && age <= 75) return "barbara";
  return "unknown";
}

export function classifyByDob(dobStr: string | null | undefined): Persona {
  if (!dobStr) return "unknown";
  const cleaned = dobStr.replace(/"/g, "");
  const dob = new Date(cleaned);
  if (isNaN(dob.getTime())) return "unknown";
  const age = Math.floor((Date.now() - dob.getTime()) / (365.25 * 86400000));
  return classifyByAge(age);
}

/** All named personas (excludes 'unknown') for iteration. */
export const NAMED_PERSONAS: Persona[] = [
  "zoe",
  "michael",
  "xavier",
  "barbara",
];

/** Persona color lookup (bg tailwind-compatible inline style). */
export function personaColorStyle(persona: Persona): {
  color: string;
  borderColor: string;
  backgroundColor: string;
} {
  const color = PERSONAS[persona].color;
  return {
    color,
    borderColor: color,
    backgroundColor: `${color}20`,
  };
}
